import React, { useState, useRef, useEffect } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Mic, Send } from "@mui/icons-material";
import PreviewModal from "./PreviewModal";
import { useTranslation } from "react-i18next";
import Lottie from 'lottie-react';
import TypingAnimation from '../../../assests/images/Animations/typing.json';
import RecordingAnimation from '../../../assests/images/Animations/recording.json';
import { FiX } from "react-icons/fi";

const NUM_RECORDING_BARS = 28;

function normalizeMetering(dB) {
    if (dB === undefined || dB === null || dB <= -55) return 0.05;
    if (dB >= 0) return 1.0;
    return 0.05 + ((dB + 55) / 55) * 0.95;
}

function ChatInputBar({
    handleSendMessage,
    handleAttachFiles,
    handleRecordingStart,
    handleRecordingStop,
    handleCancelRecording,
    setIsRecording,
    isRecording,
    isTyping,
    isTargetRecording,
    chatService,
    targetUserId,
    targetUserType,
    replyingMessage,
    clearReply,
    meteringLevelRef
}) {
    const [text, setText] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const { i18n, t } = useTranslation();

    const [recentLevels, setRecentLevels] = useState(() => Array(NUM_RECORDING_BARS).fill(0.05));
    const [recordingDuration, setRecordingDuration] = useState(0);
    const recordingTimerRef = useRef(null);
    const waveformIntervalRef = useRef(null);

    const formatRecordingTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    useEffect(() => {
        if (isRecording) {
            setRecordingDuration(0);
            setRecentLevels(Array(NUM_RECORDING_BARS).fill(0.05));

            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);

            waveformIntervalRef.current = setInterval(() => {
                const level = meteringLevelRef?.current ?? -160;
                const normalized = normalizeMetering(level);
                setRecentLevels(prev => [...prev.slice(1), normalized]);
            }, 100);
        } else {
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
            setRecordingDuration(0);
            setRecentLevels(Array(NUM_RECORDING_BARS).fill(0.05));
        }

        return () => {
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
        };
    }, [isRecording]);

    const toggleRecording = () => {
        if (isRecording) {
            handleRecordingStop();
        } else {
            handleRecordingStart();
        }
        setIsRecording(!isRecording);
    };

    const cancelRecording = () => {
        setIsRecording(false);
        handleCancelRecording();
    };

    const handleTextChange = (e) => {
        setText(e.target.value);
        if (chatService && chatService.isConnected && targetUserId && targetUserType) {
            chatService.typing(targetUserId, targetUserType);
        }
    };

    const handleFileAttach = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles((prev) => [...prev, ...files]);
        setShowPreviewModal(true);
    };

    const handleSend = () => {
        if (text.trim()) {
            handleSendMessage({ text, type: "text" });
            setText("");
        } else if (selectedFiles.length > 0) {
            handleSendFiles();
        }
    };

    const handleSendFiles = () => {
        handleAttachFiles(selectedFiles);
        setSelectedFiles([]);
        setShowPreviewModal(false);
    };

    const removeFile = (index) => {
        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(index, 1);
        setSelectedFiles(updatedFiles);
    };

    const closePreviewModal = () => {
        setSelectedFiles([]);
        setShowPreviewModal(false);
    };

    const handleClick = () => {
        if (text.trim()) {
            handleSend();
        } else {
            toggleRecording();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getIcon = () => {
        if (text.trim()) {
            return <Send style={{ fontSize: 30, color: "white" }} />;
        }
        return isRecording ? (
            <Send style={{ fontSize: 30, color: "white" }} />
        ) : (
            <Mic style={{ fontSize: 30, color: "white" }} />
        );
    };

    const getAnimation = () => {
        if (isTyping) return TypingAnimation;
        if (isTargetRecording) return RecordingAnimation;
    };

    return (
        <div className="action-bar-container">
            {replyingMessage && (
                <div className="reply-preview-bar">
                    <div className="reply-left-border" />
                    <div className="reply-content">
                        <div className="reply-header">
                            <span className="reply-label">{t('ReplyingTo')}</span>
                            <IconButton size="small" onClick={clearReply} className="close-reply-icon">
                                <FiX />
                            </IconButton>
                        </div>
                        <div className="reply-message-snippet">
                            {replyingMessage.type === 'text'
                                ? replyingMessage.text
                                : t('MediaMessage')}
                        </div>
                    </div>
                </div>
            )}
            <div className="action-bar-container2">
                {(isTyping || isTargetRecording) && (
                    <div className="typingAnimationContainer">
                        <Lottie
                            animationData={getAnimation()}
                            loop={true}
                            style={{ height: 90, width: 90 }}
                        />
                    </div>
                )}

                {isRecording ? (
                    <div className="recording-container">
                        <button className="cancel-recording-button" onClick={cancelRecording}>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <circle cx="16" cy="16" r="16" fill="#FF3B30" />
                                <path d="M11 12H21V24H11V12Z" fill="white" />
                                <path d="M9 10H23V12H9V10Z" fill="white" />
                                <path d="M13 8H19V10H13V8Z" fill="white" />
                            </svg>
                        </button>

                        <div className="recording-waveform-container">
                            {recentLevels.map((level, index) => (
                                <div
                                    key={index}
                                    className="recording-waveform-bar"
                                    style={{ height: `${Math.max(3, level * 28)}px` }}
                                />
                            ))}
                        </div>

                        <div className="recording-timer-row">
                            <span className="recording-dot-pulse" />
                            <span className="recording-timer">{formatRecordingTime(recordingDuration)}</span>
                        </div>
                    </div>
                ) : (
                    <div className="message-input-section">
                        <TextField
                            variant="outlined"
                            placeholder={t('TypeYourMessage')}
                            value={text}
                            onChange={handleTextChange}
                            onKeyDown={handleKeyPress}
                            fullWidth
                            multiline
                            maxRows={4}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "rgba(234, 237, 255, 1)",
                                    borderRadius: "32px",
                                    padding: 0,
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "12px 14px",
                                },
                            }}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton component="label">
                                            <AttachFileIcon style={{ fontSize: 26 }} />
                                            <input
                                                type="file"
                                                style={{ display: "none" }}
                                                multiple
                                                onChange={handleFileAttach}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                )}
                <button
                    className="action-button"
                    onClick={handleClick}
                    title={text.trim() ? "Send" : isRecording ? "Pause Recording" : "Start Recording"}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 64 64" className="button-circle">
                        <circle cx="32" cy="32" r="30" fill={text.trim() ? "#304FFF" : "#007BFF"} />
                    </svg>
                    <div className="icon-container">{getIcon()}</div>
                </button>
            </div>

            <PreviewModal
                open={showPreviewModal}
                files={selectedFiles}
                onClose={closePreviewModal}
                onSend={handleSendFiles}
                onRemove={removeFile}
            />
        </div>
    );
}

export default ChatInputBar;
